import { useCallback, useEffect, useRef, useState } from "react";
import {
  readAdminView,
  useAdminViewRestoring,
  writeAdminView,
} from "../../lib/admin/adminViewState";
import { apiFetch } from "../../api/client";
import { formatDate, formatRelative, shortUserAgent } from "../../lib/admin/adminFormat";

type AdminSession = {
  id: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  ip: string | null;
  user_agent: string | null;
  active: boolean;
};

export function AdminUserSessions({ userId }: { userId: string }) {
  const cacheKey = `admin:userSessions:${userId}`;
  const restoring = useAdminViewRestoring();
  /** Возврат «Назад»: список входов сразу на месте, чтобы прокрутка восстановилась. */
  const restoredRef = useRef(restoring ? readAdminView<AdminSession[]>(cacheKey) : undefined);
  const [sessions, setSessions] = useState<AdminSession[]>(restoredRef.current ?? []);
  const [loading, setLoading] = useState(!restoredRef.current);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await apiFetch<{ sessions: AdminSession[] }>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions`
      );
      writeAdminView(`admin:userSessions:${userId}`, d.sessions ?? []);
      setSessions(d.sessions ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось загрузить входы");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // userId у компонента постоянный (карточка пересоздаётся при смене пользователя).
    if (restoredRef.current) return;
    void load();
  }, [load]);

  async function revoke(sessionId: string) {
    setBusyId(sessionId);
    setErr("");
    try {
      await apiFetch(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(
          sessionId
        )}/revoke`,
        { method: "POST" }
      );
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось отозвать вход");
    } finally {
      setBusyId(null);
    }
  }

  const activeCount = sessions.filter((s) => s.active).length;

  return (
    <>
      <h3 className="wb-admin-dossier__section">
        Входы ({sessions.length}
        {activeCount ? `, активных ${activeCount}` : ""})
      </h3>
      {err ? (
        <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
          {err}
        </p>
      ) : null}
      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Устройство</th>
              <th>IP</th>
              <th>Когда</th>
              <th>Состояние</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td title={s.user_agent ?? ""}>{shortUserAgent(s.user_agent)}</td>
                <td className="wb-admin-table__mono">{s.ip ?? "—"}</td>
                <td title={formatDate(s.created_at)}>{formatRelative(s.created_at)}</td>
                <td>
                  {s.revoked_at
                    ? "отозван"
                    : s.active
                      ? "активен"
                      : "истёк"}
                </td>
                <td>
                  {s.active ? (
                    <button
                      type="button"
                      className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                      disabled={busyId === s.id}
                      onClick={() => void revoke(s.id)}
                    >
                      {busyId === s.id ? "…" : "Отозвать"}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!sessions.length && !loading ? (
              <tr>
                <td colSpan={5}>Входов нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
